import React, { useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import BoletoStatus from './BoletoStatus';
import { FieldSet } from '../../elements';
import DownloadButton from '../DownloadButton';
import NewBoletoButton from './NewBoletoButton';

const BOLETO_QUERY = gql`
  query Boleto($id: ID!) {
    boleto(id: $id) {
      id
      pdfUrl
      status
      dueDate
      formattedTypableLine
      logs {
        id
        detailedMessage
      }
    }
  }
`;

const PAYMENT_METHOD_LABELS = {
  '1': 'Boleto',
  '2': 'Cartão de Crédito',
  '6': 'Pix',
};

const formatDateTime = (isoString) => {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatDate = (isoString) => {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
};

export const Safe2PayPagamento = ({ safe2payPagamento }) => {
  if (!safe2payPagamento) return null;
  const { pago, paid_at, payment_date, cancelled_at, payment_method_code, payment_method_display } = safe2payPagamento;
  const methodLabel = PAYMENT_METHOD_LABELS[payment_method_code] || payment_method_display;

  const statusLabel = cancelled_at ? 'Cancelado' : pago ? 'Pago' : 'Aguardando pagamento';
  const statusColor = cancelled_at ? '#999' : pago ? 'limegreen' : '#b8860b';

  return (
    <>
      <FieldSet title="Pagamento" />
      <p>
        <strong>Status:</strong>{' '}
        <span style={{ color: statusColor, fontWeight: 'bold' }}>
          {statusLabel}
        </span>
      </p>
      {pago && payment_date && (
        <p>
          <strong>Data de pagamento:</strong> {formatDateTime(payment_date)}
        </p>
      )}
      {pago && paid_at && (
        <p>
          <strong>Data de repasse:</strong> {formatDate(paid_at)}
        </p>
      )}
      {cancelled_at && (
        <p>
          <strong>Cancelado em:</strong> {formatDateTime(cancelled_at)}
        </p>
      )}
      <p>
        <strong>Método:</strong> {methodLabel}
      </p>
      <p style={{ color: '#555', fontSize: '0.85em' }}>Processado via Safe2Pay</p>
    </>
  );
};

const isSafe2PayNonBoleto = (safe2payPagamento, boletoId) =>
  safe2payPagamento &&
  safe2payPagamento.payment_method_code !== '1' &&
  (safe2payPagamento.pago || !boletoId);

const Boleto = ({ id, resubmitting, onResubmit, userCanGenerateBoletos, safe2payPagamento }) => {
  const skip = isSafe2PayNonBoleto(safe2payPagamento, id) || !id;
  const { loading, error, data, refetch } = useQuery(BOLETO_QUERY, {
    variables: { id },
    skip,
  });
  const boleto = get(data, 'boleto') || {};

  useEffect(() => {
    if (skip) return;
    const interval = setInterval(() => refetch(), 3000);
    return () => clearInterval(interval);
  }, [skip, id, refetch]);

  if (skip) {
    return <Safe2PayPagamento safe2payPagamento={safe2payPagamento} />;
  }

  if (error) {
    return (
      <>
        <FieldSet title="Pagamento" />
        <p className="red">Ocorreu um erro ao carregar o boleto.</p>
        <NewBoletoButton disabled={resubmitting} onClick={onResubmit} />
      </>
    );
  }

  if (userCanGenerateBoletos && get(boleto, 'logs', []).length > 0) {
    return (
      <>
        <FieldSet title="Pagamento" />
        {boleto.logs.map((log) => (
          <p key={log.id} className="red">
            {log.detailedMessage}.
          </p>
        ))}
          <NewBoletoButton disabled={resubmitting} onClick={onResubmit} />
      </>
    );
  }
  if (
    userCanGenerateBoletos &&
    boleto.status === 'OVERDUE'
  ) {
    return (
      <>
        <FieldSet title="Pagamento" />
        <p>Boleto vencido, favor clicar no botão para gerar um novo boleto</p>
        <NewBoletoButton disabled={resubmitting} onClick={onResubmit} />
      </>
    );
  }
  if (userCanGenerateBoletos && (loading || boleto.status === 'UNAVAILABLE')) {
    return (
      <>
        <FieldSet title="Pagamento" />
        <p>Por favor, aguarde alguns instantes até que o boleto seja gerado.</p>
        <NewBoletoButton disabled={resubmitting} onClick={onResubmit} />
      </>
    );
  }

  return (
    <>
      <FieldSet title="Pagamento" />
      <BoletoStatus status={boleto.status} />
      <DownloadButton
        href={boleto.pdfUrl}
        label="Baixar boleto"
        filename="boleto.pdf"
      />
      {userCanGenerateBoletos && (
        <NewBoletoButton disabled={resubmitting} onClick={onResubmit} />
      )}
    </>
  );
};

Boleto.propTypes = {
  id: PropTypes.number,
  resubmitting: PropTypes.bool,
  onResubmit: PropTypes.func,
  userCanGenerateBoletos: PropTypes.bool,
  safe2payPagamento: PropTypes.shape({
    payment_method_code: PropTypes.string,
    payment_method_display: PropTypes.string,
    pago: PropTypes.bool,
    paid_at: PropTypes.string,
    payment_date: PropTypes.string,
    cancelled_at: PropTypes.string,
  }),
};

Boleto.defaultProps = {
  id: undefined,
  onResubmit: () => null,
  resubmitting: false,
  userCanGenerateBoletos: false,
  safe2payPagamento: null,
};

export default Boleto;
